import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { LegalDocument01Icon, LockKeyIcon, Shield01Icon } from "@hugeicons/core-free-icons";
import { HEADLINE_BENCHMARK } from "@saucer/core";
import { useEffect } from "react";
import { navigate, useRoute } from "../router.js";
import { SaucerMark } from "./logo.js";

/**
 * The chrome the marketing pages share — the landing and the pricing page.
 * It lived inside the landing until pricing needed the same nav and the same
 * footer; two copies of a footer is how a stale link survives a rename.
 */

export const go = (path: string) => (event: React.MouseEvent) => {
  event.preventDefault();
  navigate(path);
};

/**
 * Section anchors only work on the landing, so they carry the path with them.
 * From /pricing, "#faq" would scroll nowhere.
 */
const anchor = (hash: string) => `/${hash}`;

/**
 * The launch strip, attached to the top of the nav rather than floating in the
 * page under it.
 *
 * It lives inside `SaasNav` so it sticks with the bar and appears on every
 * marketing page, which is the point — an announcement only on the landing is
 * one most visitors never see. Solid `--accent` on `--accent-ink`: those two
 * tokens invert together between themes, so the strip is a black bar with white
 * type in light mode and the reverse in dark, and never needs a second colour.
 *
 * The long half is hidden on narrow screens rather than wrapped, because a
 * two-line announcement bar pushes the whole page down on a phone.
 *
 * Hidden on the page it points at: a banner advertising the page you are
 * already reading is just a row of wasted height.
 */
function LaunchStrip() {
  const path = useRoute();
  if (path.startsWith("/managed")) return null;

  return (
    <a className="saas-strip" href="/managed" onClick={go("/managed")}>
      <span className="saas-strip__tag">New</span>
      <span className="saas-strip__text">
        Managed ClickHouse &amp; Postgres — publish a SQL query as an API that answers in{" "}
        <strong>{HEADLINE_BENCHMARK.p50}&nbsp;ms</strong>
      </span>
      <span className="saas-strip__short">Managed ClickHouse &amp; Postgres</span>
      <span className="saas-strip__go" aria-hidden="true">
        →
      </span>
    </a>
  );
}

/** A quiet top bar: mark + wordmark, section links, two small buttons. */
export function SaasNav() {
  return (
    <header className="saas-nav">
      <LaunchStrip />
      <nav className="saas-nav__inner saas-wrap-5xl">
        <a className="saas-nav__brand" href="/" onClick={go("/home")}>
          <SaucerMark size={20} />
          saucer.sh
        </a>
        <div className="saas-nav__links">
          <a href={anchor("#features")}>Features</a>
          <a className="saas-nav__new" href="/managed" onClick={go("/managed")}>
            Databases <i aria-hidden="true">New</i>
          </a>
          <a href="/metrics" onClick={go("/metrics")}>
            Metrics
          </a>
          <a href="/pricing" onClick={go("/pricing")}>
            Pricing
          </a>
          <a href={anchor("#faq")}>FAQ</a>
          <a href="/docs" onClick={go("/docs")}>
            Docs
          </a>
        </div>
        <div className="saas-nav__actions">
          <a className="saas-nav__ghost" href="/login" onClick={go("/login")}>
            Sign in
          </a>
          <a className="saas-nav__cta" href="/login" onClick={go("/login")}>
            Start free
          </a>
        </div>
      </nav>
    </header>
  );
}

export function SaasFooter() {
  return (
    <footer className="saas-footer">
      <div className="saas-wrap-5xl">
        <div className="saas-footer__grid">
          <div className="saas-footer__about">
            <span className="saas-footer__logo">
              <SaucerMark size={22} /> saucer.sh
            </span>
            <p>
              Every file you make deserves a URL. saucer puts it there from your terminal, your
              browser or your clipboard — and keeps it there.
            </p>
            <span className="saas-beta">
              <i aria-hidden="true" /> Free to start, no card
            </span>
            <TrustMarks />
          </div>
          <div className="saas-footer__cols">
            <div className="saas-footer__col">
              <span className="saas-footer__head">Product</span>
              <a href="/" onClick={go("/home")}>
                Home
              </a>
              <a href="/login" onClick={go("/login")}>
                Sign in
              </a>
              <a href="/pricing" onClick={go("/pricing")}>
                Pricing
              </a>
              <a href="/metrics" onClick={go("/metrics")}>
                Metrics
              </a>
              <a href={anchor("#faq")}>FAQ</a>
            </div>
            <div className="saas-footer__col">
              <span className="saas-footer__head">Developers</span>
              <a href="/docs" onClick={go("/docs")}>
                Docs
              </a>
              <a href="/managed" onClick={go("/managed")}>
                Managed databases
              </a>
              <a href="/managed/docs" onClick={go("/managed/docs")}>
                Database docs
              </a>
              <a href="/docs#agents" onClick={go("/docs")}>
                Agent skill
              </a>
              <a href="/install.sh">Install script</a>
              <a href="https://github.com/bas3line/saucer">GitHub</a>
            </div>
            <div className="saas-footer__col">
              <span className="saas-footer__head">The CLI</span>
              <a href="/docs" onClick={go("/docs")}>
                saucer send
              </a>
              <a href="/docs" onClick={go("/docs")}>
                saucer paste
              </a>
              <a href="/docs" onClick={go("/docs")}>
                saucer snap
              </a>
              <a href="/docs" onClick={go("/docs")}>
                saucer search
              </a>
            </div>
            <div className="saas-footer__col">
              <span className="saas-footer__head">Legal</span>
              <a href="/privacy" onClick={go("/privacy")}>
                Privacy
              </a>
              <a href="/terms" onClick={go("/terms")}>
                Terms
              </a>
              <a href="/dmca" onClick={go("/dmca")}>
                DMCA
              </a>
            </div>
          </div>
        </div>
        <div className="saas-footer__bar">
          <span>© 2026 saucer.sh</span>
          <span>Built on Cloudflare Workers</span>
        </div>
      </div>
      <div className="saas-footer__word" aria-hidden="true">
        <span className="saas-footer__mark">
          <SaucerMark size={30} />
        </span>
        saucer.sh
      </div>
    </footer>
  );
}

/**
 * The trust marks under the wordmark: four icons, no prose.
 *
 * Deliberately not certification seals. SOC 2, SOC 1 and ISO 27001 are audit
 * outcomes, and their marks are licensed only to organisations holding a
 * completed report — printing one saucer does not hold would be claiming an
 * auditor's attestation rather than making a claim of its own, and enterprise
 * buyers read those seals as procurement evidence. If those reports ever
 * exist, this is the row they belong in, using the assets the auditor issues.
 *
 * What is here instead is checkable in this repository: `routes/serve.ts` sets
 * the sandbox CSP and `nosniff` on served bytes, `hashSecret` in
 * `packages/core/src/ids.ts` is the only way a credential is written, and the
 * retention and sharing sections of `privacy.tsx` carry the last two. Every
 * mark should stay something a reader could go and verify.
 *
 * They are shaped like seals on purpose — a ringed disc with a stamped word
 * under it is the visual language a buyer scans a footer for. The shape is
 * borrowed; the claims underneath it are ours and are true.
 *
 * The stamp is one or two words, so the full claim lives in `title` for the
 * pointer and in visually-hidden text for a screen reader. Not `aria-label` on
 * the link: that would replace the visible stamp for voice control, so someone
 * saying "click GDPR" would find nothing.
 */
/** A seal is drawn either from the icon set or from a downloaded asset. */
type MarkBase = { stamp: string; label: string; href?: string };
type Mark = (MarkBase & { icon: IconSvgElement }) | (MarkBase & { img: string });

const MARKS: Mark[] = [
  {
    icon: Shield01Icon,
    stamp: "Sandboxed",
    label: "Sandboxed delivery — every served file carries default-src 'none'; sandbox and nosniff",
  },
  {
    icon: LockKeyIcon,
    stamp: "SHA-256",
    label: "Hashed credentials — sessions, CLI tokens and link passwords stored as SHA-256 digests",
  },
  {
    // The real European flag, not a line icon standing in for one:
    // commons.wikimedia.org/wiki/File:Flag_of_Europe.svg, public domain, saved
    // unmodified at public/gdpr-eu.svg. It is the only mark here that is a
    // downloaded asset, and it is safe to use precisely because nobody issues
    // a GDPR certificate — there is no auditor's seal to misappropriate, and
    // the flag alongside our own claim asserts no EU endorsement.
    img: "/gdpr-eu.svg",
    stamp: "GDPR",
    label: "GDPR — erasure on request; delete a file and the link dies at once",
    href: "/privacy",
  },
  {
    icon: LegalDocument01Icon,
    stamp: "No resale",
    label: "No data resale — we do not sell or rent personal data to third parties",
    href: "/privacy",
  },
];

function TrustMarks() {
  return (
    <ul className="seals" aria-label="Security and data protection">
      {MARKS.map((mark) => {
        const art = "img" in mark;
        const face = (
          <>
            <span className={`seals__disc${art ? " seals__disc--art" : ""}`} aria-hidden="true">
              {"img" in mark ? (
                <img className="seals__art" src={mark.img} alt="" width={40} height={40} />
              ) : (
                <HugeiconsIcon icon={mark.icon} size={17} strokeWidth={1.6} />
              )}
            </span>
            <span className="seals__stamp">{mark.stamp}</span>
          </>
        );

        return (
          <li className="seals__item" key={mark.stamp}>
            {mark.href ? (
              <a className="seals__face" href={mark.href} onClick={go(mark.href)} title={mark.label}>
                {face}
                <span className="seals__sr">{mark.label}</span>
              </a>
            ) : (
              <span className="seals__face" title={mark.label}>
                {face}
                <span className="seals__sr">{mark.label}</span>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Reveals the big grids as they scroll into view, and grows the latency bars
 * with them. Lives here rather than in the landing because /managed uses the
 * same bento and needs the same behaviour — two copies of this is how one page
 * ends up with bars that never animate.
 */
export function useReveal() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const targets = document.querySelectorAll(
      ".bento, .saas-pricing, .saas-faq, .saas-cta__card",
    );
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal--in");
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px" },
    );
    const timers: number[] = [];
    for (const el of targets) {
      el.classList.add("reveal");
      io.observe(el);
      // Anchor jumps and fast scrolling can skip intersection entirely;
      // nothing is allowed to stay invisible, whatever the scroll did.
      timers.push(window.setTimeout(() => el.classList.add("reveal--in"), 2200));
    }
    return () => {
      io.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, []);
}
